/**
 * Image Search Modal Component
 * ============================
 * Modal for searching and selecting product images from Google.
 */

import { useState, useEffect, useCallback } from 'react'
import { X, Search, Loader2, Check, AlertTriangle } from 'lucide-react'
import { imageSearchApi } from '@/services/api'
import type { ImageSearchResult } from '@/types'

interface ImageSearchModalProps {
  query: string
  onSelect: (imageUrl: string) => void
  onClose: () => void
}

export default function ImageSearchModal({ query, onSelect, onClose }: ImageSearchModalProps) {
  const [images, setImages] = useState<ImageSearchResult[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Search for images when modal opens
  useEffect(() => {
    const searchImages = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await imageSearchApi.search(query, 20)
        setImages(response.images)
        // Check for API error in response
        if (response.error) {
          setError(response.error)
        } else if (response.images.length === 0) {
          setError('No images found. Try a different search query.')
        }
      } catch (err) {
        console.error('Image search failed:', err)
        setError('Failed to search for images. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    searchImages()
  }, [query])

  // Handle image selection
  const handleSelect = useCallback(() => {
    if (selectedImage) {
      onSelect(selectedImage)
      onClose()
    }
  }, [selectedImage, onSelect, onClose])

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Search size={20} className="text-gray-500 dark:text-gray-400" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Search Images
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Query: "{query}"
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title="Close (Esc)"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={40} className="animate-spin text-primary-500 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Searching for images...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <AlertTriangle size={40} className="text-amber-500 mb-4" />
              <p className="text-red-500 dark:text-red-400 text-center max-w-md">{error}</p>
              {error.includes('quota') && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center max-w-md">
                  The daily API quota has been reached. Image search will be available again after midnight Pacific Time.
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
              {images.map((image, index) => (
                <div
                  key={`${image.url}-${index}`}
                  className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                    selectedImage === image.url
                      ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                  onClick={() => setSelectedImage(image.url)}
                  title={image.width && image.height ? `${image.width}×${image.height}px` : image.title}
                >
                  {/* Thumbnail */}
                  <div className="aspect-square bg-gray-100 dark:bg-gray-700">
                    <img
                      src={image.thumbnail_url}
                      alt={image.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        // Try original URL as fallback
                        const target = e.target as HTMLImageElement
                        if (target.src !== image.url) {
                          target.src = image.url
                        }
                      }}
                    />
                  </div>

                  {/* Image size indicator */}
                  {image.width && image.height && (
                    <div className={`absolute bottom-0 left-0 right-0 text-center py-0.5 text-[10px] font-medium ${
                      image.width >= 800 || image.height >= 800
                        ? 'bg-green-500/90 text-white'
                        : image.width >= 400 || image.height >= 400
                          ? 'bg-yellow-500/90 text-white'
                          : 'bg-gray-500/90 text-white'
                    }`}>
                      {image.width}×{image.height}
                    </div>
                  )}

                  {/* Selection indicator */}
                  {selectedImage === image.url && (
                    <div className="absolute top-2 right-2 bg-primary-500 text-white rounded-full p-1">
                      <Check size={14} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {images.length > 0 && `${images.length} images found`}
            {selectedImage && ' • 1 selected'}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedImage}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                selectedImage
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              <Check size={16} />
              Add to Product
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
